import React, { useEffect, useState } from 'react'
import DropZoneContainer from '../../services/DropZoneContainer'
import { faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons'
import { useDropzone } from 'react-dropzone'
import Button from 'ui-components/lib/components/Button'
import { Flex, Box } from 'reflexbox'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useAlertStack from 'ui-components/lib/hooks/useAlertStack'
import { useMutation } from '@apollo/react-hooks'
import { S3_UPLOADER } from '../../graphql/mutations/demands'
import { fileExtension } from '../../utils'
import Spinner from 'ui-components/lib/components/Spinner'
import Typography from 'ui-components/lib/components/Typography'
import fileS3Exists from "../../services/fileS3Exists"

const MAX_FILES = 3
const MAX_FILE_SIZE = 15728640 // 15MB
const ALLOWED_FORMATS = '.pdf, .png, .jpeg, .jpg, .xls, .xlsx, .doc, .docx, .ppt, .pptx'

const DropzoneServices = ({ question, questionIndex, formik, onLoad = () => {} }) => {
  const { showMessage } = useAlertStack()

  const [isFilesUploading, setUploadingFiles] = useState(false)
  useEffect(() => {
    onLoad(isFilesUploading)
  }, [onLoad, isFilesUploading])

  const currentAttachments = formik.values.answers[questionIndex]?.attachments || []
  const atLimit = currentAttachments.length >= MAX_FILES
  const remainingSlots = Math.max(MAX_FILES - currentAttachments.length, 0)

  const addAttachments = (newAttachment = {}) => {
    const answers = formik.values.answers

    answers[questionIndex] = {
      ...formik.values.answers[questionIndex],
      attachments: [...formik.values.answers[questionIndex].attachments, newAttachment],
    }

    formik.setValues({
      ...formik.values,
      answers,
    })
  }

  const [uploadFile] = useMutation(S3_UPLOADER)

  const onDrop = async (accepted) => {
    if (remainingSlots <= 0) {
      showMessage({
        title: 'Limite de arquivos atingido',
        message: `Você já anexou o máximo de ${MAX_FILES} arquivos.`,
        color: 'danger',
        position: 'bottom-left',
        time: 4000,
      })
      return
    }

    const filesToUpload = accepted.slice(0, remainingSlots)

    if (accepted.length > remainingSlots) {
      showMessage({
        title: 'Limite de arquivos excedido',
        message: `Apenas ${remainingSlots} arquivo${remainingSlots > 1 ? 's' : ''} ${remainingSlots > 1 ? 'foram aceitos' : 'foi aceito'}. O limite é de ${MAX_FILES} arquivos no total.`,
        color: 'warning',
        position: 'bottom-left',
        time: 4000,
      })
    }

    if (!filesToUpload.length) return

    setUploadingFiles(true)

    for await (let file of filesToUpload) {
      const { data: { s3uploader: { signedUrl, randomFileName } } = {} } = await uploadFile({
        variables: { fileName: file.name, mimeType: file.type, extension: fileExtension.format(file.name) },
      })

      const response = await fetch(signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      })

      let uploaded = false
      if (response.ok) {
        const path = new URL(signedUrl)
        uploaded = await fileS3Exists(path.pathname)
      }

      if (uploaded) {
        addAttachments({
          name: file.name,
          randomFileName,
          attachment: null,
          mime: file.type,
          extension: fileExtension.format(file.name),
          fileSize: file.size,
        })
      } else {
        showMessage({
          title: 'Falha de upload do arquivo',
          message: 'Ocorreu um erro durante o upload de um ou mais arquivos, porfavor, tente novamente.',
          color: 'danger',
          position: 'bottom-left',
          time: 4000,
        })
        break
      }
    }

    setUploadingFiles(false)
  }

  // eslint-disable-next-line
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ALLOWED_FORMATS,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    maxFiles: remainingSlots || 1,
    disabled: isFilesUploading || atLimit,
    onDropRejected() {
      const message = atLimit
        ? `Você já anexou o máximo de ${MAX_FILES} arquivos.`
        : `O arquivo foi rejeitado porque excede o tamanho máximo de ${MAX_FILE_SIZE / 1024 / 1024} megabytes, ou foram selecionados mais arquivos do que o permitido (${remainingSlots} restante${remainingSlots > 1 ? 's' : ''}).`

      showMessage({
        title: 'Falha ao selecionar um ou mais arquivos',
        message,
        color: 'danger',
        position: 'bottom-left',
        time: 4000,
      })
    },
  })

  return (
    <DropZoneContainer
      className="dropzone"
      aria-required={question.required}
      onDrop={(accepted, rejected, event) => onDrop(accepted, rejected, event, questionIndex)}
      isDragActive={isDragActive && !isFilesUploading && !atLimit}
      mt={2}
      ml={0}
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <div>
        <center>
          <p>Você pode arrastar, ou clicar no botão para enviar até {MAX_FILES} arquivos.</p>
          <p>Os formatos suportados são {ALLOWED_FORMATS}.</p>
          <p>O limite do tamanho de cada arquivo não deve exceder {MAX_FILE_SIZE / 1024 / 1024} megabytes.</p>
          {atLimit && (
            <p>Limite de {MAX_FILES} arquivos atingido.</p>
          )}
          <Box>
            <Button p={[1, 2]} disabled={isFilesUploading || atLimit} color="primary">
              <Flex justifyContent="center" alignItems="center" flexWrap="wrap">
                {!isFilesUploading && (
                  <>
                    <FontAwesomeIcon icon={faCloudUploadAlt} />
                    <Flex mr={2} />
                  </>
                )}
                {isFilesUploading ? <Spinner color="primary" /> : 'Selecionar arquivo'}
              </Flex>
            </Button>
          </Box>
        </center>
      </div>
      {!isFilesUploading && !!currentAttachments.length && (
        <>
          <Flex mb={2} />
          <Typography>Arquivos:</Typography>
        </>
      )}
      {!isFilesUploading && (
        <>
          {currentAttachments.map((file) => (
            <Flex flexDirection="row" key={file.randomFileName || file.name}>
              <Typography style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '250px' }}>
                {file.name.substring(0, file.name.length - 4)}
              </Typography>
              <Typography>
                {file.name.substring(file.name.length - 4, file.name.length)} {'('}
                {Number(file.fileSize / 1024 / 1024).toFixed(2)} mb{')'}
              </Typography>
            </Flex>
          ))}
        </>
      )}
    </DropZoneContainer>
  )
}

export default DropzoneServices
